import { Button, Stack, Theme, Typography } from '@mui/material'
import Formatter from '../../helpers/Formatter'
import { QueryStatus } from '../hooks/useQuery'
import { ChartDataWithUnit } from './ChartsHelper'
import HorizontalBarChart from './HorizontalBarChart'

type ChartValidData = number[] | number[][]

export const BarChartResult = <T extends ChartValidData,>({ data, status, theme, refetch }: { data: ChartDataWithUnit<'bar', T, string> & { categories?: string[] }, status: QueryStatus, theme: Theme, refetch?: () => void }) => {
    switch (status) {
        case 'success':
            return (
                <HorizontalBarChart
                    data={data}
                    options={{
                        indexAxis: 'x',
                        scales: {
                            x: {
                                grid: {
                                    display: false
                                }
                            },
                            y: {
                                beginAtZero: false,
                                grid: {
                                    display: false
                                }
                            }
                        },
                        plugins: {
                            datalabels: {
                                color: theme.palette.text.secondary,
                                anchor: 'end',
                                align: 'end',
                                display: 'auto',
                                offset: 10,
                                formatter: (value, context) => {
                                    const delta = value[1] - value[0]
                                    const category = data.categories[context.dataIndex] ?? ''
                                    const formattedDelta = Formatter.compact(delta)
                                    const prefix = delta > 0 ? '+' : ''

                                    return `${prefix}${formattedDelta} $ PMPM\n ${category}`
                                }
                            },
                            legend: { display: false },
                            tooltip: {
                                callbacks: {
                                    beforeLabel: item => (item.chart.data.datasets[item.datasetIndex] as any).categories?.[item.dataIndex] || ''
                                },
                                axis: 'y'
                            },
                            annotation: {
                                annotations: data?.datasets[0]?.data?.length > 0
                                    ? {}
                                    : {
                                            label1: {
                                                type: 'label',
                                                content: ['No data available for the selected time period'],
                                                position: 'center',
                                                color: theme.palette.text.secondary
                                            }
                                        }
                            }
                        },
                        layout: {
                            padding: {
                                top: 40
                            }
                        }
                    }}
                />
            )

        case 'loading':
            return (
                <HorizontalBarChart
                    isLoading={true}
                    data={{
                        datasets: [],
                        labels: []
                    }}
                />
            )
        case 'error':
            return (
                <Stack spacing={2} sx={{ justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
                    <Typography>
                        There was an error during the data fetching
                    </Typography>
                    <Button variant='contained' color='error' onClick={() => refetch?.()}>
                        Retry
                    </Button>
                </Stack>
            )
    }
}
